import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';

import { VendorVariantMediaModel } from '@codegen-basetypes';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import Image from '@components/shared/Image/Image';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import useWindowSize from '@hooks/useWindowSize/useWindowSize';
import DesignAwardBadge, { DesignAwardPlace } from '@components/shared/DesignAwards/DesignAwardBadge';
import { canZoomImage } from '../MobileImageZoom/zoomHelper';
import MagnifyingGlassIcon from './assets/magnifying-glass.svg';
import styles from './ProductImageCarousel.module.scss';

SwiperCore.use([Pagination, Navigation]);

type IImage = Pick<VendorVariantMediaModel, 'id' | 'uri' | 'height' | 'width'>;
interface IProductImageCarouselProps {
  images: IImage[];
  altText?: string;
  setActiveZoomImage?: (image: IImage) => void;
  designAwardPlace?: DesignAwardPlace;
}

const ProductImageCarousel = ({
  images,
  altText = 'Product Image',
  setActiveZoomImage,
  designAwardPlace
}: IProductImageCarouselProps) => {
  const { width: windowWidth } = useWindowSize();

  const showZoomButton = (image: IImage) => {
    if (windowWidth && image.height && image.width) {
      const viewPort = {
        height: windowWidth, // set to windowWidth on purpose
        width: windowWidth
      };

      const targetImage = {
        height: image.height,
        width: image.width
      };

      return canZoomImage(viewPort, targetImage);
    }
    return false;
  };

  return (
    <Swiper
      className={styles.Carousel}
      slidesPerView={1.15}
      spaceBetween={8}
      loop
      centeredSlides
    >
      {images.map((image, idx) => (
        <SwiperSlide key={image.id}>
          <AspectRatioBox className={styles.ImageContainer}>
            { (idx === 0 && !!designAwardPlace) && (
              <DesignAwardBadge
                place={designAwardPlace}
                className={styles.DesignAwardBadge}
              />
            )}
            {showZoomButton(image) && (
              <button
                type="button"
                className={styles.ZoomButton}
                onClick={() => {
                  if (setActiveZoomImage) {
                    setActiveZoomImage(image);
                    analyticsTrack(
                      `zoom_clicked`, {
                        image,
                      }
                    );
                  }
                }}
              >
                <MagnifyingGlassIcon />
              </button>
            )}

            <Image
              src={image.uri}
              alt={altText ? `${altText} - Image ${idx}` : 'Product'}
              sizes="90vw"
              fit="fit-in"
              layout="fill"
              placeholder="blur"
            />
          </AspectRatioBox>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default ProductImageCarousel;
